import React, {useRef, useState} from 'react';
import styled from 'styled-components';
import {COLORS} from '../../../constants/colors';

interface RippleProps {
  as?:
    | keyof JSX.IntrinsicElements
    | React.ComponentType<React.PropsWithChildren>;
  onClick?: React.MouseEventHandler;
  className?: string;
  hidden?: boolean;
  disabled?: boolean;
  theme?: 'light' | 'dark';
}

interface StateProps {
  top: string;
  left: string;
  height: string;
  width: string;
}

export const Ripple = React.forwardRef<
  HTMLElement,
  React.PropsWithChildren<RippleProps>
>(function Ripple({children, hidden, theme, ...rest}, ref) {
  const [state, setState] = useState<StateProps[]>([]);
  const bounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Adds styles indexed for each ripple to the state.
   * The style added includes the exact position co-ordinate position and the height and width of the container
   */
  const showRipple: React.MouseEventHandler<HTMLDivElement> = event => {
    if (rest.disabled) {
      return;
    }

    const rippleContainer = event.currentTarget;
    const size = rippleContainer.offsetWidth;
    const pos = rippleContainer.getBoundingClientRect();
    const x = event.clientX - pos.x - size / 2;
    const y = event.clientY - pos.y - size / 2;

    const spanStyles = {
      top: y + 'px',
      left: x + 'px',
      height: size + 'px',
      width: size + 'px',
    };

    setState(state.concat(spanStyles));
  };

  /**
   * Render each indexed styles as a Span (which animates/scales out as a Ripple)
   */
  const renderRippleSpan = () => {
    if (!state.length) {
      return null;
    }

    return state.map((spanStyles, index) => (
      <span
        data-testid="atomRippleInnerElement"
        key={'spanCount_' + index}
        style={spanStyles}
      />
    ));
  };

  /**
   * Remove the ripples after a fixed timeout allowing enough time for the animation to complete
   */
  const cleanUpRipples = () => {
    if (bounce.current) {
      clearTimeout(bounce.current);
    }

    bounce.current = setTimeout(() => {
      setState([]);
    }, 850); // => clean up ripples after animation ends
  };

  if (hidden) {
    return null;
  }

  return (
    <RippleWrapper {...rest} ref={ref}>
      {children}
      <RippleContainer
        onMouseDown={showRipple}
        onMouseUp={cleanUpRipples}
        $theme={theme}
      >
        {renderRippleSpan()}
      </RippleContainer>
    </RippleWrapper>
  );
});

const RippleWrapper = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  will-change: transform, opacity;
  cursor: pointer;
  outline: none;
  border: 0;
`;

const RippleContainer = styled.div<{$theme: RippleProps['theme']}>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  span {
    transform: scale(0);
    border-radius: 100%;
    position: absolute;
    opacity: 0.75;
    background-color: ${({$theme}) =>
      $theme === 'dark' ? COLORS.Grey1 : COLORS.White};
    animation: ripple 850ms;

    @keyframes ripple {
      to {
        opacity: 0;
        transform: scale(5);
      }
    }
  }
`;
